import * as React from 'react'
import cx from 'classnames'
import styles from './ViewHeading.sass'
import { animated, FrameValue } from 'react-spring'
import Icon from 'components/Icon'
import { useAnimationProgress } from 'contexts/AnimationProgressContext'
import { createSpringLerp, lerp } from 'utils/math-util'

export type ViewHeadingSize = 'normal' | 'small'

export interface ViewHeadingProps {
  primaryText: React.ReactNode
  secondaryText: React.ReactNode
  visual?: React.ReactNode
  isDropdown?: boolean
  /**
   * A value from 0 (full) to 1 (compact) for controlling
   * the compaction animation. Defaults to 0. Can also be provided via the AnimationProgressContext..
   */
  compactionAnimationProgress?: FrameValue<number>
  viewHeadingSize?: ViewHeadingSize
}

export function ViewHeading({
  primaryText,
  secondaryText,
  visual,
  isDropdown,
  compactionAnimationProgress,
  viewHeadingSize = 'normal',
}: ViewHeadingProps) {
  compactionAnimationProgress = useAnimationProgress(
    compactionAnimationProgress,
  )

  const springLerp = createSpringLerp(compactionAnimationProgress)
  const primaryStyle = {
    transform: springLerp(0, 16).to((v) => `translateY(${v}px)`),
    opacity: springLerp(1, 0),
  }
  const secondaryStyle = {
    transform: compactionAnimationProgress.to(
      (v) => `translateY(${lerp(0, -2, v)}px) scale(${lerp(1, 0.8333334, v)})`, // Aproximate scaling for 24px -> 16px
    ),
  }
  const visualStyle = {
    transform: compactionAnimationProgress.to(
      (v) =>
        `translate(${lerp(0, -8, v)}px, ${lerp(0, 8, v)}px) scale(${lerp(
          1,
          0.75,
          v,
        )})`,
    ),
  }
  const contentStyle = {
    // If we have a visual, it takes up space. But when we scale it down,
    // there is no reflow happening, which means the whitespace between the
    // text and the visual will be too wide. This offsets that.
    transform: visual ? springLerp(0, -16).to((v) => `translateX(${v}px)`) : '',
  }
  return (
    <div className={cx(styles.root, isDropdown && styles.clickable)}>
      {visual && (
        <animated.div className={styles.visual} style={visualStyle}>
          {visual}
        </animated.div>
      )}
      <animated.div className={styles.content} style={contentStyle}>
        <animated.p className={styles.primary} style={primaryStyle}>
          {primaryText}
        </animated.p>
        <animated.p
          className={cx(styles.secondary, styles[viewHeadingSize])}
          style={secondaryStyle}
        >
          <span className={styles.secondaryText}>{secondaryText}</span>
          {isDropdown && (
            <span className={styles.icon}>
              <Icon
                name="arrow"
                rotate={90}
                size={viewHeadingSize === 'small' ? 16 : 24}
              />
            </span>
          )}
        </animated.p>
      </animated.div>
    </div>
  )
}
